<template>
<div v-if="dataLoaded">
  <div class="header-info-row">
    <PageHeader :title="pageTitle" />
    <div class="type-select" ref="typeSelectRef">
      <div class="type-select__trigger" @click="typeOpen = !typeOpen">
        <span>{{ schools.type || "미분류" }}</span>
        <span class="td-caret" aria-hidden="true">▼</span>
      </div>
      <div v-if="typeOpen" class="type-select__dropdown">
        <div
          v-for="opt in typeOptions"
          :key="opt"
          class="type-select__item"
          @click="selectType(opt)"
        >
          {{ opt }}
        </div>
      </div>
      <v-btn
        v-if="typeDirty"
        @click="saveType()"
        variant="flat"
        size="small"
        class="btn-primary type-select__save"
      >저장</v-btn>
    </div>

    <div class="memo-field">
      <input
        v-model="schools.memo"
        type="text"
        spellcheck="false"
        placeholder="메모 입력"
        class="memo-field__input"
      />
      <v-btn
        v-if="memoDirty"
        @click="saveMemo()"
        variant="flat"
        size="small"
        class="btn-primary"
      >저장</v-btn>
    </div>
  </div>

  <div class="row-2col">
  <FormCard class="section-card">
    <template #header>
      <div class="card-header-row">
        <span>계약 정보</span>
        <div class="card-header-actions">
          <template v-if="viewChecks[1]">
            <v-btn @click="onClickContractInfo" variant="flat" color="primary" size="small">확인</v-btn>
            <v-btn @click="onClickEdit(1)" variant="outlined" size="small">취소</v-btn>
          </template>
          <v-btn v-else @click="onClickEdit(1)" variant="outlined" size="small">수정</v-btn>
        </div>
      </div>
    </template>

  <!-- 데스크탑 ≥640px: 테이블 -->
  <table v-if="!isMobile" class="info-table" style="max-width: 100%">
    <tr>
      <td class="table-field" style="width: 150px">계정 생성일</td>
      <td class="table-field" style="width: 180px">계약시작일</td>
      <td class="table-field" style="width: 180px">계약만료일</td>
      <td class="table-field">학생수</td>
      <td class="table-field" style="width: 160px">lms 계정개수</td>
      <td class="table-field" style="width: 160px">student 계정개수</td>
    </tr>
    <tr>
      <td class="table-data edit__off">{{ accountCreatedDate }}</td>
      <td class="table-data padding__zero relative">
        <input
          v-if="viewChecks[1]"
          @click="viewDate[0] = !viewDate[0]"
          v-model="contractInfo.startDate"
          class="input_editable-date"
          spellcheck="false"
          type="text"
          readonly="true"
        />
        <div v-else>{{ contractInfo.startDate || "-" }}</div>

        <div v-if="viewChecks[1]" class="calendar"></div>
        <div ref="startRef" class="wrap-datepicker" style="top: 50px">
          <DatePicker
            v-if="viewDate[0]"
            :max-date="contractInfo.endDate"
            v-model="contractInfo.startDate"
            color="blue"
          />
        </div>
      </td>

      <td class="table-data padding__zero relative">
        <input
          v-if="viewChecks[1]"
          @click="viewDate[1] = !viewDate[1]"
          v-model="contractInfo.endDate"
          class="input_editable-date"
          spellcheck="false"
          type="text"
          readonly="true"
        />
        <div v-else>{{ contractInfo.endDate || "-" }}</div>

        <div v-if="viewChecks[1]" class="calendar"></div>
        <div ref="endRef" class="wrap-datepicker" style="top: 50px">
          <DatePicker
            v-if="viewDate[1]"
            :min-date="contractInfo.startDate"
            v-model="contractInfo.endDate"
            color="blue"
          />
        </div>
      </td>
      <td class="table-data edit__off">
        {{ contractInfo.currentStudentNumber || "-" }}
      </td>
      <td class="table-data edit__off">{{ contractInfo.classCount || "-" }}</td>
      <td class="table-data edit__off">
        {{ contractInfo.contractStudentNumber || "-" }}
      </td>
    </tr>
  </table>

  <!-- 모바일 ≤640px: 스택 -->
  <div v-else>
    <div class="contract-period__item" style="margin-bottom: 8px">
      <v-icon icon="mdi-calendar-check-outline" size="16" class="contract-period__icon" />
      <div class="contract-period__col">
        <span class="contract-period__label">계정 생성일</span>
        <span class="tabular-nums contract-period__date">{{ accountCreatedDate }}</span>
      </div>
    </div>
    <!-- 계약 기간 강조 박스 -->
    <div class="contract-period">
      <div class="contract-period__item">
        <v-icon icon="mdi-calendar-start" size="16" class="contract-period__icon" />
        <div class="contract-period__col">
          <span class="contract-period__label">시작</span>
          <div v-if="viewChecks[1]" class="relative">
            <input
              @click="viewDate[0] = !viewDate[0]"
              v-model="contractInfo.startDate"
              class="input_editable-date mobile-input"
              spellcheck="false"
              type="text"
              readonly="true"
            />
            <div ref="startRef" class="wrap-datepicker mobile-datepicker">
              <DatePicker
                v-if="viewDate[0]"
                :max-date="contractInfo.endDate"
                v-model="contractInfo.startDate"
                color="blue"
              />
            </div>
          </div>
          <span v-else class="tabular-nums contract-period__date">
            {{ contractInfo.startDate || "—" }}
          </span>
        </div>
      </div>
      <div class="contract-period__divider" />
      <div class="contract-period__item">
        <v-icon icon="mdi-calendar-end" size="16" class="contract-period__icon" />
        <div class="contract-period__col">
          <span class="contract-period__label">만료</span>
          <div v-if="viewChecks[1]" class="relative">
            <input
              @click="viewDate[1] = !viewDate[1]"
              v-model="contractInfo.endDate"
              class="input_editable-date mobile-input"
              spellcheck="false"
              type="text"
              readonly="true"
            />
            <div ref="endRef" class="wrap-datepicker mobile-datepicker">
              <DatePicker
                v-if="viewDate[1]"
                :min-date="contractInfo.startDate"
                v-model="contractInfo.endDate"
                color="blue"
              />
            </div>
          </div>
          <span v-else class="tabular-nums contract-period__date">
            {{ contractInfo.endDate || "—" }}
          </span>
        </div>
      </div>
    </div>

    <!-- 학생/계정 카운트 3-up -->
    <div class="contract-stats">
      <div class="contract-stat">
        <span class="contract-stat__value tabular-nums">{{ contractInfo.currentStudentNumber || "—" }}</span>
        <span class="contract-stat__label">학생 수</span>
      </div>
      <div class="contract-stat">
        <span class="contract-stat__value tabular-nums">{{ contractInfo.classCount || "—" }}</span>
        <span class="contract-stat__label">LMS 계정</span>
      </div>
      <div class="contract-stat">
        <span class="contract-stat__value tabular-nums">{{ contractInfo.contractStudentNumber || "—" }}</span>
        <span class="contract-stat__label">Student 계정</span>
      </div>
    </div>
  </div>
  </FormCard>

  <ContactComponent :contactlist="contactInfo" :contactlist_origin="contactInfo" />
  </div>

  <FormCard class="section-card">
    <template #header>학급 정보</template>

  <!-- 데스크탑 ≥640px: 테이블 -->
  <div v-if="!isMobile" class="table-wrap">
    <table class="data-table">
      <tr>
        <td class="th">학년</td>
        <td class="th">반</td>
        <td class="th">출판사</td>
        <td class="th">학생수</td>
        <td class="th">영어교사</td>
        <td class="th">담임교사</td>
        <td class="th tdlast">학생 계정</td>
      </tr>
      <tr v-for="(j, i) in classInfo" :key="i">
        <td class="td">{{ j.grade || "-" }}</td>
        <td class="td">{{ j.classNum || "-" }}</td>
        <td class="td">{{ j.publisher || "-" }}</td>
        <td class="td">{{ j.studentCnt || "-" }}</td>
        <td class="td">{{ j.engTeacher || "-" }}</td>
        <td class="td">{{ j.teacher || "-" }}</td>
        <td class="td tdlast">
          <button
            ref="dotBtn"
            class="iddetailbtn"
            @click="onClickDetailInfo(j.grade, j.classNum)"
          >
            •&nbsp;•&nbsp;•
          </button>
        </td>
      </tr>
    </table>
  </div>

  <!-- 모바일 ≤640px: 학급 카드 -->
  <div v-else class="class-cards">
    <article v-for="(j, i) in classInfo" :key="i" class="class-card">
      <header class="class-card__head">
        <div class="class-card__badge">
          <span class="class-card__badge-grade">{{ j.grade || "-" }}</span>
          <span class="class-card__badge-suffix">학년</span>
        </div>
        <div class="class-card__title">
          <span class="class-card__class">{{ j.classNum || "-" }}반</span>
          <span class="class-card__publisher">{{ j.publisher || "—" }}</span>
        </div>
        <button
          class="class-card__action"
          @click="onClickDetailInfo(j.grade, j.classNum)"
          aria-label="계정 관리"
        >
          <v-icon icon="mdi-dots-horizontal" size="18" />
        </button>
      </header>
      <dl class="class-card__list">
        <div class="class-card__row">
          <dt>
            <v-icon icon="mdi-account-multiple-outline" size="14" />
            학생
          </dt>
          <dd class="tabular-nums">{{ j.studentCnt || "—" }}명</dd>
        </div>
        <div class="class-card__row">
          <dt>
            <v-icon icon="mdi-translate" size="14" />
            영어
          </dt>
          <dd>{{ j.engTeacher || "—" }}</dd>
        </div>
        <div class="class-card__row class-card__row--full">
          <dt>
            <v-icon icon="mdi-account-tie" size="14" />
            담임
          </dt>
          <dd>{{ j.teacher || "—" }}</dd>
        </div>
      </dl>
    </article>
  </div>

  <ClassInfoPopup2
    v-if="$store.state.classInfoPopupView"
    :classInfo="studentInfo"
  />
  </FormCard>
</div>
</template>
<script setup>
import { ref, reactive, onMounted, onBeforeUnmount, watch, computed } from "vue";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";
import { onClickOutside } from "@vueuse/core";
import { DatePicker } from "v-calendar";
import dayjs from "dayjs";
import ClassInfoPopup2 from "../../../popup/ClassInfoPopupComponent2.vue";
import FormCard from "@/components/ui/FormCard.vue";
import PageHeader from "@/components/ui/PageHeader.vue";
import ContactComponent from "../../../userAccountManage/schoolManage/schoolDetail/ContactComponent.vue";
import { SCHOOL_TYPE_OPTIONS } from "@/constants/schoolType";
import { getSchoolCreate } from "@/api/guest-api-list";
import "../../../../assets/style/wrapStyle.css";
import "../../../../assets/style/tableStyle.css";
import "../../../../assets/style/buttonStyle.css";
// import '../../../../assets/style/dropdownStyle.css'

const store = useStore();
const router = useRouter();
const route = useRoute();

const code = ref("");
// console.log(code)

// 데이터 fetch 완료 전에는 본문 lazy 마운트 (SchoolDetailComponent.vue와 동일한 이유:
// 자식 컴포넌트가 빈 초기값으로 먼저 마운트되면 dirty 비교 스냅샷이 잘못 찍힘)
const dataLoaded = ref(false);

const schools = ref({});
const contractInfo = ref({});
const classInfo = ref([]);
const studentInfo = ref([]);
const contactInfo = ref([]);

const pageTitle = computed(() => {
  const parts = [schools.value.schoolName, schools.value.schoolCode].filter(Boolean);
  return parts.length ? parts.join(" ") : "GUEST 상세";
});

// 계정 생성일 — SchoolDetailComponent.vue(유료) accountIssueDate와 동일한 규칙:
// createdAt → _id timestamp 순으로 fallback
const accountCreatedDate = computed(() => {
  let millis = 0;
  if (schools.value?.createdAt) {
    const t = new Date(schools.value.createdAt).getTime();
    if (!Number.isNaN(t)) millis = t;
  }
  if (!millis && typeof schools.value?._id === "string" && schools.value._id.length >= 8) {
    const secs = parseInt(schools.value._id.substring(0, 8), 16);
    if (Number.isFinite(secs)) millis = secs * 1000;
  }
  if (!millis) return "-";
  const d = new Date(millis);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}/${mm}/${dd}`;
});

// 분류 드롭다운 — 현재 학교의 type이 옵션 목록에 없는 값(과거 수기 입력 등)이어도
// 드롭다운에 표시되도록 목록 앞에 끼워넣는다. (renewal a317ddb 개선 유지)
const typeOptions = computed(() => {
  const current = (schools.value?.type ?? "").trim();
  return current && !SCHOOL_TYPE_OPTIONS.includes(current)
    ? [current, ...SCHOOL_TYPE_OPTIONS]
    : SCHOOL_TYPE_OPTIONS;
});
const typeOpen = ref(false);
const originalType = ref("");
const typeDirty = computed(() => (schools.value.type ?? "") !== originalType.value);

const selectType = (opt) => {
  schools.value.type = opt;
  typeOpen.value = false;
};

// 메모 — 유료 상세(SchoolDetailComponent.vue)와 동일하게 전용 API(UPDATE_SCHOOL_MEMO)로 저장
const originalMemo = ref("");
const memoDirty = computed(() => (schools.value.memo ?? "") !== originalMemo.value);

const saveMemo = async () => {
  const memo = schools.value.memo ?? "";
  const res = await store.dispatch("UPDATE_SCHOOL_MEMO", {
    schoolCode: code.value,
    memo,
  });
  if (res) {
    originalMemo.value = memo;
  } else {
    alert("메모 저장 중 오류가 발생하였습니다.");
    schools.value.memo = originalMemo.value;
  }
};

const saveType = async () => {
  if (!window.confirm("분류를 변경하시겠습니까?")) return;
  const data = {
    office: schools.value.office,
    schoolCode: code.value,
    schoolName: schools.value.schoolName,
    payType: schools.value.payType || "-",
    payStatus: schools.value.payStatus || "-",
    accountStatus: schools.value.accountStatus || "-",
    affiliate: schools.value.affiliate || "-",
    language: schools.value.language,
    type: schools.value.type ?? "",
  };
  const res = await store.dispatch("UPDATE_SCHOOL_INFO", data);
  if (res) {
    alert("분류가 변경되었습니다.");
    originalType.value = schools.value.type ?? "";
  } else {
    alert("분류 변경 중 오류가 발생하였습니다.");
    schools.value.type = originalType.value;
  }
};

// 수정 flag
const viewChecks = ref([false, false]);

// 편집 모드 진입 시 원본 백업 — 취소 시 복원
const contractInfoBackup = ref({});

// datepicker flag
const viewDate = ref([false, false]);

// onClickOutside 변수
const isOpen = ref(false);
const dropboxRef = ref(null);
const startRef = ref(null);
const endRef = ref(null);
const typeSelectRef = ref(null);

// 왜 해놨을까
const dotBtn = ref(null);

// 모바일 분기
const windowWidth = ref(typeof window !== "undefined" ? window.innerWidth : 1024);
const isMobile = computed(() => windowWidth.value <= 640);
const onResize = () => { windowWidth.value = window.innerWidth; };
onMounted(() => { window.addEventListener("resize", onResize); });
onBeforeUnmount(() => { window.removeEventListener("resize", onResize); });

const loadSchool = async () => {
  dataLoaded.value = false;
  try {
    // URL 파라미터를 우선 사용한다. sessionStorage의 "schoolCode"는
    // 목록 화면(SchoolBrowseView.vue 등)마다 갱신 여부가 달라 신뢰할 수 없다 —
    // 실제로 SchoolBrowseView.vue의 openDetail()은 이 값을 갱신하지 않아
    // 다른 학교로 이동해도 예전에 저장된 코드로 계속 조회되는 버그가 있었다.
    // 파라미터 없이 진입하는 구버전 경로 호환을 위해 fallback만 남긴다. (renewal 437abe6)
    code.value =
      route.params.schoolCode || JSON.parse(sessionStorage.getItem("schoolCode"));

    const res = await store.dispatch("GET_SCHOOL_DETAIL", code.value);
    const contract = await store.dispatch("GET_CONTRACT_INFO", code.value);
    const class_info = await store.dispatch("GET_GUEST_CLASS_INFO", code.value);
    const contact = await store.dispatch("GET_CONTACT_INFO", code.value);

    // GET_SCHOOL_DETAIL(단건 조회) 응답엔 memo/createdAt/_id 등 일부 필드가 통째로
    // 빠지는 경우가 있다(예: 메모 저장 후 재조회하면 사라짐, 계정 생성일 항상 "-").
    // 학교 조회 목록(getSchoolCreate)엔 이 필드들이 항상 들어있는 걸 확인했으니,
    // 목록 값을 기본값으로 깔고 단건 조회 결과로 덮어써서 누락을 메운다 —
    // 단건 조회 응답에 실제로 존재하는 값(빈 문자열 포함)은 항상 우선한다.
    const listRes = await getSchoolCreate();
    const matched = (listRes?.data?.data ?? []).find(
      (s) => s.schoolCode === code.value
    );
    schools.value = { ...(matched || {}), ...(res || {}) };
    contractInfo.value = contract[0];
    classInfo.value = class_info;
    contactInfo.value = contact.contact;
    originalType.value = schools.value.type ?? "";
    originalMemo.value = schools.value.memo ?? "";
  } catch (err) {
    console.log(err);
  } finally {
    // [3차] 모든 비동기 로드 완료 후에야 본문 마운트 허용 —
    // 자식 컴포넌트(ContactComponent 등)가 빈 초기값으로 먼저 마운트되면
    // dirty 비교용 스냅샷이 잘못 찍히는 문제 방지
    dataLoaded.value = true;
  }
};

onMounted(loadSchool);

// 목록에서 다른 GUEST 학교로 바로 이동한 경우(같은 라우트 컴포넌트 재사용) 다시 불러온다.
watch(
  () => route.params.schoolCode,
  () => loadSchool()
);

onMounted(() => {
  onClickOutside(dropboxRef, () => {
    isOpen.value = false;
  });
  onClickOutside(startRef, () => {
    viewDate.value[0] = false;
  });
  onClickOutside(endRef, () => {
    viewDate.value[1] = false;
  });
  onClickOutside(typeSelectRef, () => {
    typeOpen.value = false;
  });
});

// date click
watch(
  () => contractInfo.value.startDate,
  (newDate, oldDate) => {
    // console.log('계약 시작일 새로운 값 : ', newDate, '옛날 값 : ', oldDate)
    if (newDate === null) {
      contractInfo.value.startDate = oldDate;
      viewDate.value[0] = false;
    } else {
      contractInfo.value.startDate = dayjs(newDate).format("YYYY/MM/DD");
      viewDate.value[0] = false;
    }
  }
);

watch(
  () => contractInfo.value.endDate,
  (newDate, oldDate) => {
    // console.log('계약 만료일 새로운 값 : ', dayjs(newDate).format('YYYY/MM/DD'), '옛날 값 : ', oldDate)

    if (newDate === null) {
      contractInfo.value.endDate = oldDate;
      viewDate.value[1] = false;
    } else {
      contractInfo.value.endDate = dayjs(newDate).format("YYYY/MM/DD");
      viewDate.value[1] = false;
    }
  }
);

// onClick event
const onClickEdit = (idx) => {
  if (viewChecks.value[idx] === false) {
    // 수정 진입 — 원본 백업
    contractInfoBackup.value = { ...contractInfo.value };
    viewChecks.value[idx] = true;
  } else {
    // 취소 — 원본 복원
    contractInfo.value = { ...contractInfoBackup.value };
    viewChecks.value[idx] = false;
  }
};

const onClickContractInfo = async (e) => {
  // console.log(schoolCode.value)
  if (
    !dayjs(contractInfo.value.startDate).isBefore(contractInfo.value.endDate)
  ) {
    alert("계약시작일이 계약만료일 이후에 있습니다.");
  } else {
    if (window.confirm("수정하시겠습니까?")) {
      const data = {
        schoolCode: code.value,
        contractList: [{ ...contractInfo.value }],
      };
      // console.log('data : ', data)

      const res = await store.dispatch("POST_UPDATE_CONTRACT_INFO", data);
      console.log(res);
      if (res.result === true) {
        alert("계약 정보가 수정되었습니다.");
        window.location.reload(true);
      } else if (res === 200) {
        alert("수정에 오류가 발생했습니다.");
      } else {
        console.log(res);
      }
    } else {
      console.log("cancel");
    }
  }
};

const onClickDetailInfo = async (grade, classNum) => {
  // const student = await store.dispatch()
  console.log("click detailInfo : ", code.value, grade, classNum);
  const data = {
    code: code.value,
    grade: String(grade),
    classNum: String(classNum),
  };
  const res = await store.dispatch("GET_GUEST_STUDENT_INFO", data);

  console.log("ABC", res);
  studentInfo.value = res;
  store.commit("SET_CLASS_INFO_POPUP_VIEW", true);
};
</script>
<style scoped>
a {
  color: var(--text-primary);
}

.section-card {
  margin-bottom: var(--space-6);
}

.card-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
}

.card-header-actions {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

/* .info-table / .table-field / .table-data 는 src/assets/styles/info-table.css 글로벌 정의 사용 */

.header-info-row {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  flex-wrap: wrap;
  margin-bottom: var(--space-5);
}

.header-info-row :deep(.page-header) {
  margin-bottom: 0;
}

.row-2col {
  display: flex;
  align-items: flex-start;
  gap: var(--space-4);
  flex-wrap: wrap;
}

.row-2col > * {
  flex: 1 1 360px;
  min-width: 320px;
}

/* 학급 정보 — 유료 학교 상세(SchoolDetailComponent.vue)와 동일한 표 스타일 */
.table-wrap {
  width: 100%;
  overflow-x: auto;
}

.data-table {
  text-align: center;
  border-spacing: 0;
  min-width: 600px;
  width: 100%;
  font-size: var(--font-body-size);
  color: var(--text-primary);
}

.th {
  border: 1px solid var(--border-default);
  border-right: none;
  padding: var(--space-3) var(--space-2);
  font-size: var(--font-small-size);
  background-color: var(--bg-subtle);
  color: var(--text-secondary);
  font-weight: 600;
}

.td {
  border: 1px solid var(--border-default);
  border-right: none;
  border-top: none;
  padding: var(--space-3) var(--space-2);
  font-size: var(--font-body-size);
  color: var(--text-primary);
}

.tdlast {
  border-right: 1px solid var(--border-default);
}

.iddetailbtn {
  font-size: var(--font-body-size);
  border: 1px solid var(--border-default);
  font-weight: 600;
  background-color: var(--bg-surface-raised);
  color: var(--text-primary);
  width: 70px;
  height: 32px;
  border-radius: var(--radius-pill);
  cursor: pointer;
}

.iddetailbtn:hover {
  background-color: var(--color-primary-50);
  border-color: var(--color-primary-600);
  color: var(--color-primary-600);
}

.calendar {
  position: absolute;
  transform: translate(0, -50%);
  top: 50%;
  right: 12px;
  width: 24px;
  height: 24px;
  background-image: url("../../../../assets/image/calendar.png");
  background-size: 100%;
  z-index: -1;
}

/* ─── 모바일 스택 레이아웃 ─── */
.mobile-field-list {
  display: flex;
  flex-direction: column;
  margin: 0;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  overflow: hidden;
  background: var(--bg-surface-raised);
}

.mobile-field-row {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  font-size: var(--font-small-size);
  border-bottom: 1px solid var(--border-default);
  min-width: 0;
}

.mobile-field-row:last-child {
  border-bottom: none;
}

.mobile-field-row dt {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 110px;
  color: var(--text-tertiary);
  font-size: var(--font-caption-size);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.mobile-field-row__icon {
  color: var(--text-tertiary);
}

.mobile-field-row dd {
  margin: 0;
  flex: 1 1 auto;
  min-width: 0;
  color: var(--text-primary);
  font-weight: 500;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

.mobile-field-row--block {
  flex-direction: column;
  gap: var(--space-2);
  align-items: stretch;
  padding: var(--space-3) var(--space-4) var(--space-4);
}

.mobile-field-row--block dt {
  flex: none;
}

.mobile-field-row__memo {
  display: block;
  padding: var(--space-2) var(--space-3);
  background: var(--bg-canvas);
  border-radius: var(--radius-sm);
  color: var(--text-secondary);
  line-height: 1.5;
}

.mobile-input {
  width: 100%;
  box-sizing: border-box;
}

.mobile-datepicker {
  position: absolute;
  top: 40px;
  left: 0;
  z-index: 10;
}

.mono {
  font-family: var(--font-family-mono, "JetBrains Mono", "D2Coding", monospace);
  font-size: var(--font-mono-size);
}

.btn-primary {
  background: var(--color-primary-600) !important;
  color: var(--text-on-primary) !important;
}

.memo-field {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.memo-field__input {
  height: 28px;
  min-width: 200px;
  padding: 0 var(--space-2);
  font-size: var(--font-small-size);
  color: var(--text-primary);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-sm);
  background: var(--bg-surface-raised);
}

/* 분류(type) 드롭다운 — 유료 학교 상세(SchoolDetailComponent.vue)의 분류 드롭다운과 동일한 형태 */
.type-select {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.type-select__trigger {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  height: 28px;
  font-size: var(--font-small-size);
  font-weight: 600;
  color: var(--text-primary);
  cursor: pointer;
}

.td-caret {
  font-size: 9px;
  color: var(--text-tertiary);
}

.type-select__dropdown {
  position: absolute;
  z-index: 50;
  top: 100%;
  left: 0;
  margin-top: var(--space-1);
  min-width: 160px;
  background-color: var(--bg-surface-raised);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md);
  overflow: hidden;
}

.type-select__item {
  padding: var(--space-3) var(--space-3);
  cursor: pointer;
  font-size: var(--font-small-size);
  border-bottom: 1px solid var(--border-default);
}

.type-select__item:last-child {
  border-bottom: none;
}

.type-select__item:hover {
  background-color: var(--color-primary-50);
}

/* 계약 기간 강조 박스 */
.contract-period {
  display: flex;
  align-items: stretch;
  gap: var(--space-3);
  padding: var(--space-4);
  background: linear-gradient(135deg, var(--color-primary-50) 0%, var(--bg-surface-raised) 100%);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  margin-bottom: var(--space-3);
}

.contract-period__item {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex: 1 1 0;
  min-width: 0;
}

.contract-period__icon {
  color: var(--color-primary-600);
  flex-shrink: 0;
}

.contract-period__col {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.contract-period__label {
  font-size: var(--font-caption-size);
  color: var(--text-tertiary);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.contract-period__date {
  font-size: var(--font-small-size);
  color: var(--text-primary);
  font-weight: 600;
}

.contract-period__divider {
  width: 1px;
  background: var(--border-default);
  flex-shrink: 0;
}

/* 학생/계정 통계 3-up */
.contract-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-2);
}

.contract-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: var(--space-3);
  background: var(--bg-surface-raised);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  text-align: center;
}

.contract-stat__value {
  font-size: var(--font-h3-size);
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1;
}

.contract-stat__label {
  font-size: var(--font-caption-size);
  color: var(--text-tertiary);
  font-weight: 500;
}

/* 학급 카드 */
.class-cards {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.class-card {
  background: var(--bg-surface);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  padding: var(--space-4);
  box-shadow: var(--shadow-sm);
  transition: box-shadow 0.15s ease;
}

.class-card:hover {
  box-shadow: var(--shadow-md);
}

.class-card__head {
  display: grid;
  grid-template-columns: 56px 1fr auto;
  gap: var(--space-3);
  align-items: center;
  padding-bottom: var(--space-3);
  margin-bottom: var(--space-3);
  border-bottom: 1px solid var(--border-default);
}

.class-card__badge {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: var(--color-primary-50);
  color: var(--color-primary-700);
  border-radius: var(--radius-md);
  padding: 6px 0;
  width: 56px;
}

.class-card__badge-grade {
  font-size: 22px;
  font-weight: 700;
  line-height: 1;
}

.class-card__badge-suffix {
  font-size: 10px;
  font-weight: 600;
  color: var(--color-primary-600);
  letter-spacing: 0.3px;
  margin-top: 2px;
}

.class-card__title {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.class-card__class {
  font-size: var(--font-body-strong-size);
  font-weight: 700;
  color: var(--text-primary);
}

.class-card__publisher {
  font-size: var(--font-caption-size);
  color: var(--text-tertiary);
}

.class-card__action {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-surface-raised);
  color: var(--text-secondary);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.class-card__action:hover {
  background: var(--color-primary-50);
  color: var(--color-primary-700);
  border-color: var(--color-primary-300);
}

.class-card__list {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-3);
  margin: 0;
  font-size: var(--font-small-size);
}

.class-card__row {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.class-card__row--full {
  grid-column: 1 / -1;
}

.class-card__row dt {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: var(--font-caption-size);
  color: var(--text-tertiary);
  font-weight: 600;
}

.class-card__row dd {
  margin: 0;
  color: var(--text-primary);
  font-weight: 500;
  word-break: keep-all;
  overflow-wrap: anywhere;
}
</style>